import {supabase} from '../config/supabase'

export class SupabaseService {
  // Initialize static cache
  static statusCache = {}
  static supabase = supabase
  static userStatusesLoaded = {}
  static userStatuses = {}

  static async saveSearch(userId, keyword, results) {
    try {
      const {data, error} = await supabase
        .from('searches')
        .insert([
          {
            user_id: userId,
            keyword: keyword,
            results: results,
            created_at: new Date().toISOString()
          }
        ])
        .select()
      if (error) throw error
      return data[0]
    } catch (error) {
      console.error('Error saving search:', error)
      throw error
    }
  }

  static async getUserSearches(userId) {
    try {
      const {data, error} = await supabase
        .from('searches')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', {ascending: false})
      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching searches:', error)
      throw error
    }
  }

  // Update a specific result's title and URL in the searches.results JSONB
  static async updateSearchResultTitleAndUrl(userId, searchId, resultId, newTitle, newUrl) {
    try {
      const { data, error } = await supabase
        .from('searches')
        .select('results')
        .eq('id', searchId)
        .single()
      if (error) throw error

      const resultsArray = Array.isArray(data?.results) ? [...data.results] : []
      const index = Number(resultId)
      if (!Number.isInteger(index) || index < 0 || index >= resultsArray.length) {
        throw new Error('Invalid result index')
      }

      const current = resultsArray[index] || {}
      resultsArray[index] = {
        ...current,
        title: newTitle,
        url: newUrl
      }

      const { error: updateError } = await supabase
        .from('searches')
        .update({ results: resultsArray })
        .eq('id', searchId)
      if (updateError) throw updateError

      return resultsArray[index]
    } catch (e) {
      console.error('Error updating result title/url:', e)
      throw e
    }
  }

  static async deleteSearch(userId, searchId) {
    try {
      console.log(`Deleting search ${searchId} for user ${userId}`)
      // Delete all related content first (generated content, status updates, etc.)
      const deletePromises = [
        // Delete generated content
        supabase
          .from('generated_content')
          .delete()
          .eq('user_id', userId)
          .eq('search_id', searchId),
        // Delete backlink statuses
        supabase
          .from('backlink_status_rtk7309')
          .delete()
          .eq('user_id', userId)
          .eq('search_id', searchId),
        // Delete extracted emails
        supabase
          .from('extracted_emails')
          .delete()
          .eq('user_id', userId)
          .eq('search_id', searchId),
      ]

      // Wait for all related deletions to complete
      await Promise.all(deletePromises)

      // Finally delete the search itself
      const {error} = await supabase
        .from('searches')
        .delete()
        .eq('user_id', userId)
        .eq('id', searchId)

      if (error) {
        console.error('Error deleting search:', error)
        throw error
      }

      return true
    } catch (error) {
      console.error('Error deleting search:', error)
      return false
    }
  }

  static async saveGeneratedContent(userId, searchId, resultId, content, subject="No subject line detected") {
    try {
      // Check if content already exists
      const {data: existingData, error: checkError} = await supabase
        .from('generated_content')
        .select('id')
        .eq('user_id', userId)
        .eq('search_id', searchId)
        .eq('result_id', resultId)
        .maybeSingle()

      if (checkError && checkError.code !== 'PGRST116') throw checkError

      if (existingData) {
        // Update existing content - remove updated_at field
        const {data, error} = await supabase
          .from('generated_content')
          .update({content: content, subject: subject})
          .eq('id', existingData.id)
          .select()
        if (error) throw error
        return data[0]
      } else {
        // Insert new content
        const {data, error} = await supabase
          .from('generated_content')
          .insert([
            {
              user_id: userId,
              search_id: searchId,
              result_id: resultId,
              content: content,
              subject: subject,
              created_at: new Date().toISOString()
            }
          ])
          .select()
        if (error) throw error
        return data[0]
      }
    } catch (error) {
      console.error('Error saving generated content:', error)
      throw error
    }
  }

  static async getGeneratedContent(userId, searchId) {
    try {
      const {data, error} = await supabase
        .from('generated_content')
        .select('*')
        .eq('user_id', userId)
        .eq('search_id', searchId)
      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching generated content:', error)
      throw error
    }
  }

  static async updateGeneratedContent(userId, searchId, resultId, content, subject="No subject line detected") {
    try {
      console.log('Updating generated content:', {userId, searchId, resultId, content, subject})
      // Ensure resultId is treated consistently as a string
      const resultIdStr = String(resultId)

      // First, check if the record exists
      const {data: existingData, error: checkError} = await supabase
        .from('generated_content')
        .select('id')
        .eq('user_id', userId)
        .eq('search_id', searchId)
        .eq('result_id', resultIdStr)
        .maybeSingle()

      console.log('Existing data check:', {existingData, checkError})

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking existing content:', checkError)
        throw checkError
      }

      if (existingData) {
        // Update existing content using the ID
        console.log('Updating existing content with ID:', existingData.id)
        const {data, error} = await supabase
          .from('generated_content')
          .update({content: content, subject: subject})
          .eq('id', existingData.id)
          .select()

        if (error) {
          console.error('Error updating content:', error)
          throw error
        }
        console.log('Content updated successfully:', data)
        return data[0]
      } else {
        // If no existing record found, create a new one
        console.log('No existing content found, creating new record')
        const {data, error} = await supabase
          .from('generated_content')
          .insert([
            {
              user_id: userId,
              search_id: searchId,
              result_id: resultIdStr,
              content: content,
              subject: subject,
              created_at: new Date().toISOString()
            }
          ])
          .select()

        if (error) {
          console.error('Error inserting new content:', error)
          throw error
        }
        console.log('New content created successfully:', data)
        return data[0]
      }
    } catch (error) {
      console.error('Error updating generated content:', error)
      throw error
    }
  }

  static async saveExtractedEmails(userId, searchId, resultId, emails) {
    try {
      // Check if record exists
      const {data: existingData, error: checkError} = await supabase
        .from('extracted_emails')
        .select('id')
        .eq('user_id', userId)
        .eq('search_id', searchId)
        .eq('result_id', String(resultId))
        .maybeSingle()

      if (checkError && checkError.code !== 'PGRST116') throw checkError

      const timestamp = new Date().toISOString()

      if (existingData) {
        // Update existing record
        const {data, error} = await supabase
          .from('extracted_emails')
          .update({emails, updated_at: timestamp})
          .eq('id', existingData.id)
          .select()
        if (error) throw error
        return data[0]
      } else {
        // Insert new record
        const {data, error} = await supabase
          .from('extracted_emails')
          .insert([
            {
              user_id: userId,
              search_id: searchId,
              result_id: String(resultId),
              emails,
              created_at: timestamp,
              updated_at: timestamp
            }
          ])
          .select()
        if (error) throw error
        return data[0]
      }
    } catch (error) {
      console.error('Error saving extracted emails:', error)
      throw error
    }
  }

  static async getExtractedEmails(userId, searchId) {
    try {
      const {data, error} = await supabase
        .from('extracted_emails')
        .select('*')
        .eq('user_id', userId)
        .eq('search_id', searchId)
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching extracted emails:', error)
      return []
    }
  }
  // TODO: legacy method, refactor later
  static async saveBacklinkStatus(userId, searchId, resultId, status) {
    console.log('Saving backlink status:', {userId, searchId, resultId, status})
    // Ensure resultId is a string
    const resultIdStr = String(resultId)
    const cacheKey = `${userId}-${searchId}-${resultIdStr}`
    
    // Immediately update the cache for instant UI feedback
    this.statusCache[cacheKey] = status

    try {
      // First check if the record exists
      const {data: existingData} = await supabase
        .from('backlink_status_rtk7309')
        .select('id')
        .eq('user_id', userId)
        .eq('search_id', searchId)
        .eq('result_id', resultIdStr)
        .single()

      let result
      if (existingData) {
        // Record exists, update it
        const {data, error} = await supabase
          .from('backlink_status_rtk7309')
          .update({status: status, updated_at: new Date().toISOString()})
          .eq('id', existingData.id)
          .select()
        if (error) throw error
        result = data[0]
      } else {
        // Record doesn't exist, insert it
        const {data, error} = await supabase
          .from('backlink_status_rtk7309')
          .insert([
            {
              user_id: userId,
              search_id: searchId,
              result_id: resultIdStr,
              status: status,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          ])
          .select()
        if (error) throw error
        result = data[0]
      }

      console.log("Status update result:", result)
      return result
    } catch (error) {
      console.error('Error saving backlink status:', error)
      // Even if Supabase save fails, we've already updated the cache,
      // so the UI will reflect the change
      // Return an object that looks like a successful response
      return {
        user_id: userId,
        search_id: searchId,
        result_id: resultIdStr,
        status: status,
        updated_at: new Date().toISOString(),
        id: `local-${Date.now()}` // Generate a fake ID for local tracking
      }
    }
  }

  // TODO: legacy method, refactor later
  static async getAllBacklinkStatuses(userId, searchId) {
    try {
      console.log(`Fetching all statuses for user ${userId} and search ${searchId}`)
      const {data, error} = await supabase
        .from('backlink_status_rtk7309')
        .select('result_id, status')
        .eq('user_id', userId)
        .eq('search_id', searchId)

      if (error) {
        console.error('Error fetching statuses from Supabase:', error)
        throw error
      }

      console.log(`Found ${data.length} status records for search ${searchId}`)
      const statusMap = {}
      data.forEach(item => {
        statusMap[item.result_id] = item.status
        const cacheKey = `${userId}-${searchId}-${item.result_id}`
        this.statusCache[cacheKey] = item.status
      })

      return statusMap
    } catch (error) {
      console.error('Error fetching all backlink statuses:', error)
      const statusMap = {}
      Object.keys(this.statusCache).forEach(key => {
        if (key.startsWith(`${userId}-${searchId}-`)) {
          const resultId = key.split('-')[2]
          statusMap[resultId] = this.statusCache[key]
        }
      })
      return statusMap
    }
  }

  

  static async fetchStatusesForVisible(userId, visibleSearchIds) {
    try {
      if (!Array.isArray(visibleSearchIds) || visibleSearchIds.length === 0) {
        return {}
      }
      const { data, error } = await supabase
        .from('backlink_status_rtk7309')
        .select('search_id, status')
        .eq('user_id', userId)
        .in('search_id', visibleSearchIds)

      if (error) {
        console.error('fetchStatusesForVisible: error fetching statuses:', error)
        throw error
      }

      const rows = Array.isArray(data) ? data : []
      console.log(`fetchStatusesForVisible: fetched ${rows.length} statuses for ${visibleSearchIds.length} visible search IDs`)

      const bySearch = {}
      rows.forEach(row => {
        const sid = row.search_id
        if (!bySearch[sid]) bySearch[sid] = []
        bySearch[sid].push(row.status)
      })

      return bySearch
    } catch (e) {
      console.error('fetchStatusesForVisible: failed:', e)
      return {}
    }
  }

  

  // Persist row open/closed state per result
  static async saveRowOpenState(userId, searchId, resultId, isOpen) {
    try {
      const resultIdStr = String(resultId);
      // Check if a record exists
      const { data: existing, error: checkError } = await supabase
        .from('backlink_status_rtk7309')
        .select('id')
        .eq('user_id', userId)
        .eq('search_id', searchId)
        .eq('result_id', resultIdStr)
        .maybeSingle();
      if (checkError && checkError.code !== 'PGRST116') throw checkError;

      const timestamp = new Date().toISOString();
      if (existing) {
        const { error } = await supabase
          .from('backlink_status_rtk7309')
          .update({ is_open: isOpen, updated_at: timestamp })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('backlink_status_rtk7309')
          .insert([{ user_id: userId, search_id: searchId, result_id: resultIdStr, is_open: isOpen, created_at: timestamp, updated_at: timestamp }]);
        if (error) throw error;
      }
      return true;
    } catch (error) {
      console.error('Error saving row open state:', error);
      return false;
    }
  }

  static async getRowOpenStates(userId, searchId) {
    try {
      const { data, error } = await supabase
        .from('backlink_status_rtk7309')
        .select('result_id, is_open')
        .eq('user_id', userId)
        .eq('search_id', searchId);
      if (error) throw error;
      const map = {};
      (data || []).forEach(row => { map[row.result_id] = !!row.is_open; });
      return map;
    } catch (error) {
      console.error('Error loading row open states:', error);
      return {};
    }
  }

  // Email Logs
  static async logSentEmail(userId, searchId, resultId, toEmail, subject, sentAtIso) {
    try {
      const resultIdValue = (resultId == null ? null : String(resultId));
      const { error } = await supabase
        .from('email_logs')
        .insert([{ user_id: userId, search_id: searchId, result_id: resultIdValue, to_email: toEmail, subject: subject, sent_at: sentAtIso }]);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error logging sent email:', error);
      return false;
    }
  }

  static async getEmailLogsForSearch(userId, searchId) {
    try {
      const { data, error } = await supabase
        .from('email_logs')
        .select('result_id, to_email, subject, sent_at')
        .eq('user_id', userId)
        .eq('search_id', searchId)
        .order('sent_at', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching email logs:', error);
      return [];
    }
  }

  // Link Directory Methods
  static async getLinkDirectoryOpportunities() {
    try {
      const {data, error} = await supabase
        .from('link_directory_ld92x4')
        .select('*')
        .order('created_at', {ascending: false})
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching link directory opportunities:', error)
      throw error
    }
  }

  static async checkDirectoryAccess(userId) {
    try {
      const {data, error} = await supabase
        .from('user_directory_access_ld92x4')
        .select('id')
        .eq('user_id', userId)
        .single()
      if (error && error.code !== 'PGRST116') {
        throw error
      }
      return !!data // Return true if access record exists
    } catch (error) {
      console.error('Error checking directory access:', error)
      return false
    }
  }


  static async updateDirectoryOpportunityStatus(opportunityId, status) {
    try {
      const {data, error} = await supabase
        .from('link_directory_ld92x4')
        .update({status: status})
        .eq('id', opportunityId)
        .select()
      if (error) throw error
      return data[0]
    } catch (error) {
      console.error('Error updating directory opportunity status:', error)
      throw error
    }
  }

  static async reportBrokenLink(userId, opportunityId, opportunityTitle) {
    try {
      const {data, error} = await supabase
        .from('broken_link_reports')
        .insert([
          {
            user_id: userId,
            opportunity_id: opportunityId,
            opportunity_title: opportunityTitle,
            reported_at: new Date().toISOString()
          }
        ])
        .select()
      if (error) throw error
      return data[0]
    } catch (error) {
      console.error('Error reporting broken link:', error)
      throw error
    }
  }

  // User Link Directory Actions
  static async getUserLinkDirectoryActions(userId) {
    try {
      const {data, error} = await supabase
        .from('user_link_directory_actions')
        .select('*')
        .eq('user_id', userId)
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching user link directory actions:', error)
      return []
    }
  }

  static async saveLinkDirectoryAction(userId, linkId, favorited, status) {
    try {
      // Check if record exists
      const {data: existingData, error: checkError} = await supabase
        .from('user_link_directory_actions')
        .select('id')
        .eq('user_id', userId)
        .eq('link_id', linkId)
        .maybeSingle()

      if (checkError && checkError.code !== 'PGRST116') throw checkError

      const timestamp = new Date().toISOString()

      if (existingData) {
        // Update existing record
        const {data, error} = await supabase
          .from('user_link_directory_actions')
          .update({favorited, status, updated_at: timestamp})
          .eq('id', existingData.id)
          .select()
        if (error) throw error
        return data[0]
      } else {
        // Insert new record
        const {data, error} = await supabase
          .from('user_link_directory_actions')
          .insert([
            {
              user_id: userId,
              link_id: linkId,
              favorited,
              status,
              created_at: timestamp,
              updated_at: timestamp
            }
          ])
          .select()
        if (error) throw error
        return data[0]
      }
    } catch (error) {
      console.error('Error saving link directory action:', error)
      throw error
    }
  }

  static async saveLinkOpenState(userId, linkId, isOpen) {
    try {
      const { data: existing, error: checkError } = await supabase
        .from('user_link_directory_actions')
        .select('id')
        .eq('user_id', userId)
        .eq('link_id', linkId)
        .maybeSingle();
      if (checkError && checkError.code !== 'PGRST116') throw checkError;

      const timestamp = new Date().toISOString();
      if (existing) {
        const { error } = await supabase
          .from('user_link_directory_actions')
          .update({ is_open: isOpen, updated_at: timestamp })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_link_directory_actions')
          .insert([{ user_id: userId, link_id: linkId, is_open: isOpen, created_at: timestamp, updated_at: timestamp }]);
        if (error) throw error;
      }
      return true;
    } catch (error) {
      console.error('Error saving link open state:', error);
      return false;
    }
  }

  static async getDirectoryActionStats(userId) {
    try {
      // Get counts of submitted and live statuses
      const {data, error} = await supabase
        .from('user_link_directory_actions')
        .select('status, count')
        .eq('user_id', userId)
        .in('status', ['Submitted', 'Live'])
        .group('status')

      if (error) throw error

      // Format the response
      const stats = {submitted: 0, live: 0}
      data.forEach(item => {
        if (item.status === 'Submitted') {
          stats.submitted = parseInt(item.count)
        } else if (item.status === 'Live') {
          stats.live = parseInt(item.count)
        }
      })

      return stats
    } catch (error) {
      console.error('Error fetching directory action stats:', error)
      return {submitted: 0, live: 0}
    }
  }

  // Email logs for Link Directory (unique per user/link)
  static async logSentEmailForLink(userId, linkId, toEmail, subject, sentAtIso) {
    try {
      const { data: existing, error: checkError } = await supabase
        .from('email_logs')
        .select('id')
        .eq('user_id', userId)
        .eq('link_id', linkId)
        .maybeSingle();
      if (checkError && checkError.code !== 'PGRST116') throw checkError;

      if (!existing) {
        const { error } = await supabase
          .from('email_logs')
          .insert([{ user_id: userId, link_id: linkId, to_email: toEmail, subject, sent_at: sentAtIso }]);
        if (error) throw error;
      }
      return true;
    } catch (error) {
      console.error('Error logging sent email for link:', error);
      return false;
    }
  }

  // Directory Content Generation
  static async saveDirectoryGeneratedContent(userId, opportunityId, content, subject="No subject line detected") {
    try {
      const timestamp = new Date().toISOString()

      // Use upsert with onConflict to handle both insert and update cases
      const {data, error} = await supabase
        .from('directory_generated_content_ld92x4')
        .upsert(
          {
            user_id: userId,
            opportunity_id: opportunityId,
            content,
            subject,
            created_at: timestamp
          },
          {onConflict: 'user_id,opportunity_id', ignoreDuplicates: false}
        )
        .select()

      if (error) throw error
      return data[0]
    } catch (error) {
      console.error('Error saving directory generated content:', error)
      throw error
    }
  }

  static async getDirectoryGeneratedContent(userId, opportunityId) {
    try {
      const {data, error} = await supabase
        .from('directory_generated_content_ld92x4')
        .select('content, subject')
        .eq('user_id', userId)
        .eq('opportunity_id', opportunityId)
        .maybeSingle()

      if (error && error.code !== 'PGRST116') throw error
      return data || null
    } catch (error) {
      console.error('Error fetching directory generated content:', error)
      return null
    }
  }

  // Get user personalization data
  static async getUserPersonalization(userId, searchId) {
    try {
      let searchData = null;
      
      // Get search-specific customization if available (only if searchId is provided)
      if (searchId) {
        const {data, error} = await supabase
          .from('searches')
          .select('custom_name, custom_title, custom_company, custom_email, custom_website_url, custom_prompt')
          .eq('id', searchId)
          .single();
          
        if (!error || error.code === 'PGRST116') {
          searchData = data;
        } else {
          console.error('Error fetching search data:', error);
        }
      }

      // Get default user values
      const {data: defaultData, error: defaultError} = await supabase
        .from('user_outreach_defaults_k9l8x3')
        .select('default_name, default_title, default_company, default_email, default_website_url, webhook_url')
        .eq('user_id', userId)
        .single();
        
      if (defaultError && defaultError.code !== 'PGRST116') {
        console.error('Error fetching default user data:', defaultError);
      }

      console.log('User personalization data retrieved:', {searchData, defaultData, userId, searchId});
      
      // Combine data with search values taking precedence
      return {
        name: (searchData?.custom_name || defaultData?.default_name || ''),
        title: (searchData?.custom_title || defaultData?.default_title || ''),
        company: (searchData?.custom_company || defaultData?.default_company || ''),
        email: (searchData?.custom_email || defaultData?.default_email || ''),
        website: (searchData?.custom_website_url || defaultData?.default_website_url || ''),
        webhook_url: (defaultData?.webhook_url || ''),
        custom_prompt: (searchData?.custom_prompt || '')
      };
    } catch (error) {
      console.error('Error fetching user personalization data:', error);
      return {
        name: '',
        title: '',
        company: '',
        email: '',
        website: '',
        webhook_url: '',
        custom_prompt: ''
      };
    }
  }
}

export default SupabaseService